import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Clock, 
  Users, 
  Award, 
  Star, 
  CheckCircle, 
  Heart, 
  Target, 
  Globe, 
  MapPin, 
  Phone, 
  Mail,
  Sparkles,
  Zap,
  ThumbsUp,
  Compass,
  Headphones,
  Map
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import strLogo from "@/assets/str-logo.png";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useEffect, useState } from "react";
import { getIcon } from "@/lib/iconMapper";

const About = () => {
  const { getConfig } = useSiteConfig();
  const [countersVisible, setCountersVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setCountersVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Get CMS content with fallbacks
  const statsData = getConfig('about_stats', [
    { icon: "Star", number: "10+", label: "Years Experience", suffix: "", color: "from-yellow-400 to-orange-500" },
    { icon: "Users", number: "5000", label: "Happy Travelers", suffix: "+", color: "from-primary to-primary/80" },
    { icon: "Award", number: "4.9", label: "Rating", suffix: "/5", color: "from-green-400 to-emerald-500" },
    { icon: "Headphones", number: "24", label: "Support", suffix: "/7", color: "from-blue-400 to-cyan-500" }
  ]);

  const valuesData = getConfig('about_values', [
    { icon: "Shield", title: "Safety First", description: "Your security is our top priority. All vehicles are regularly inspected and drivers are professionally trained.", gradient: "from-blue-500 to-cyan-500" },
    { icon: "Heart", title: "Customer Love", description: "We treat every guest like family. Your comfort and satisfaction drive everything we do.", gradient: "from-pink-500 to-rose-500" },
    { icon: "Zap", title: "Swift & Reliable", description: "Punctuality is our promise. We monitor flights and ensure timely pickups, every single time.", gradient: "from-yellow-500 to-orange-500" },
    { icon: "Compass", title: "Local Experts", description: "Born and raised in DR, we share insider knowledge and hidden gems with our guests.", gradient: "from-green-500 to-emerald-500" }
  ]);

  const teamData = getConfig('about_team', [
    { icon: "Users", name: "Professional Drivers", role: "The Heart of Our Service", description: "Our drivers are more than chauffeurs—they are ambassadors of Dominican hospitality. Fluent in multiple languages, knowledgeable about local culture, and committed to your comfort." },
    { icon: "Headphones", name: "24/7 Support Team", role: "Always Here for You", description: "Our dedicated support team is available around the clock. Whether you need to modify a booking or require assistance during your trip, we are just a call away." },
    { icon: "Map", name: "Tour Specialists", role: "Crafting Experiences", description: "Our tour experts design unforgettable experiences. From hidden waterfalls to cultural immersions, they ensure every adventure exceeds expectations." }
  ]);

  const whyChooseUsData = getConfig('about_why_items', [
    "Fully licensed & insured operations",
    "Modern, air-conditioned vehicle fleet",
    "24/7 customer support availability",
    "Professional, multilingual drivers",
    "Real-time flight monitoring",
    "No hidden fees or surprises",
    "Flexible cancellation policies",
    "Child seats available on request"
  ]);

  // CMS text content
  const heroBadge = getConfig('about_hero_badge', 'Premium Transfer & Tour Service');
  const heroTitle = getConfig('about_hero_title', 'About Servicio Turístico Rachel');
  const heroDescription = getConfig('about_hero_description', 'Your gateway to authentic Dominican Republic experiences. Premium transportation, unforgettable tours, and genuine Caribbean hospitality since 2014.');
  const storyBadge = getConfig('about_story_badge', 'Our Story');
  const storyTitle = getConfig('about_story_title', 'Connecting Travelers to Paradise Since 2014');
  const storyParagraph1 = getConfig('about_story_paragraph_1', 'Servicio Turístico Rachel was born from a deep love for the Dominican Republic and a desire to share its magic with the world. What began as a simple airport transfer service has grown into a comprehensive travel experience company.');
  const storyParagraph2 = getConfig('about_story_paragraph_2', 'Our founder, Rachel, started with a single van and a commitment to exceptional service. Today, we operate a modern fleet and have helped thousands of travelers discover the beauty of the Caribbean.');
  const storyParagraph3 = getConfig('about_story_paragraph_3', 'Every journey with us is more than transportation—it is the beginning of an adventure, a connection to local culture, and memories that last a lifetime.');
  const missionTitle = getConfig('about_mission_title', 'Our Mission');
  const missionText = getConfig('about_mission_text', 'To provide safe, luxurious, and memorable transportation experiences that showcase the best of the Dominican Republic while treating every guest like family.');
  const visionTitle = getConfig('about_vision_title', 'Our Vision');
  const visionText = getConfig('about_vision_text', 'To be the most trusted and beloved transfer and tour service in the Caribbean, known for exceptional quality, genuine hospitality, and unforgettable experiences.');
  const ctaTitle = getConfig('about_cta_title', 'Ready to Experience the Difference?');
  const ctaDescription = getConfig('about_cta_description', 'Join thousands of happy travelers who have discovered the beauty of Dominican Republic with us.');

  const AnimatedCounter = ({ target, suffix, isVisible }: { target: number, suffix: string, isVisible: boolean }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!isVisible) return;
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current * 10) / 10);
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }, [isVisible, target]);
    
    return <span>{target === 4.9 ? count.toFixed(1) : Math.floor(count)}{suffix}</span>;
  };

  return (
    <Layout>
      <Helmet>
        <title>About Us | Servicio Turístico Rachel - Premium Transfers & Tours</title>
        <meta name="description" content="Discover Servicio Turístico Rachel - your trusted partner for premium airport transfers, luxury tours, and authentic Dominican Republic experiences since 2014." />
      </Helmet>

      {/* Hero Section with Animated Background */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-secondary"></div>
        
        {/* Animated Blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-tropical/30 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-palm/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-1/4 left-1/4 animate-float">
          <Sparkles className="w-8 h-8 text-tropical/60" />
        </div>
        <div className="absolute bottom-1/3 right-1/4 animate-float animation-delay-2000">
          <Star className="w-6 h-6 text-white/40" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-float animation-delay-4000">
          <Globe className="w-10 h-10 text-palm/40" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            {/* Animated Logo */}
            <div className="mb-8 flex justify-center animate-fade-in-up">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 blur-2xl rounded-full animate-pulse-glow"></div>
                <img 
                  src={strLogo} 
                  alt="Servicio Turístico Rachel" 
                  className="h-28 w-auto relative z-10 drop-shadow-2xl"
                />
              </div>
            </div>
            
            {/* Badge with Animation */}
            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 text-lg py-2 px-6 animate-fade-in-up animation-delay-200">
              ✨ {heroBadge}
            </Badge>
            
            {/* Animated Title */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-400">
              <span className="block">About</span>
              <span className="bg-gradient-to-r from-tropical via-white to-palm bg-clip-text text-transparent">
                Servicio Turístico Rachel
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto animate-fade-in-up animation-delay-600">
              {heroDescription}
            </p>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {statsData.map((stat: any, index: number) => {
              const IconComponent = getIcon(stat.icon);
              const numericValue = typeof stat.number === 'string' ? parseFloat(stat.number) : stat.number;
              return (
                <div 
                  key={index}
                  className="group animate-fade-in-up"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-card/80 backdrop-blur-sm">
                    {/* Gradient Border Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.color || 'from-primary to-blue-600'} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                    
                    <CardContent className="pt-8 pb-6 text-center relative">
                      {/* Animated Icon */}
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color || 'from-primary to-blue-600'} mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Animated Counter */}
                      <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-2">
                        <AnimatedCounter target={numericValue} suffix={stat.suffix || ''} isVisible={countersVisible} />
                      </div>
                      
                      <div className="text-sm md:text-base font-medium text-muted-foreground">
                        {stat.label}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="animate-fade-in-up">
              <Badge className="mb-6 bg-gradient-to-r from-secondary to-tropical text-white border-0 text-base py-2 px-5">
                <Sparkles className="w-4 h-4 mr-2" />
                {storyBadge}
              </Badge>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-foreground">Connecting Travelers to</span>
                <span className="block bg-gradient-to-r from-primary via-secondary to-tropical bg-clip-text text-transparent">
                  Caribbean Paradise
                </span>
              </h2>
              
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p className="animate-fade-in-up animation-delay-200">
                  {storyParagraph1}
                </p>
                <p className="animate-fade-in-up animation-delay-400">
                  {storyParagraph2}
                </p>
                <p className="animate-fade-in-up animation-delay-600">
                  {storyParagraph3}
                </p>
              </div>
            </div>

            {/* Image Grid */}
            <div className="relative animate-fade-in-up animation-delay-400">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80" 
                      alt="Beautiful Dominican Beach"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80" 
                      alt="Palm Trees"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80" 
                      alt="Tropical Paradise"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&q=80" 
                      alt="Crystal Clear Water"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 md:left-auto md:-right-6 bg-gradient-to-br from-primary to-secondary text-white p-6 rounded-2xl shadow-2xl max-w-xs animate-float z-20">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-6 h-6" />
                  <span className="font-bold text-lg">Based in Punta Cana</span>
                </div>
                <p className="text-sm opacity-90">Serving all Dominican Republic destinations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Mission Card */}
            <Card className="group relative overflow-hidden border-2 border-primary/20 hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in-up">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardHeader className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl md:text-3xl text-primary">{missionTitle}</CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {missionText}
                </p>
              </CardContent>
            </Card>

            {/* Vision Card */}
            <Card className="group relative overflow-hidden border-2 border-secondary/20 hover:border-secondary/50 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in-up animation-delay-200">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardHeader className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-red-500 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl md:text-3xl text-secondary">{visionTitle}</CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {visionText}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-tropical to-palm text-white border-0 text-base py-2 px-6">
              <Heart className="w-4 h-4 mr-2" />
              What Drives Us
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-foreground">Our Core</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Values</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide every decision we make and every journey we create.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valuesData.map((value: any, index: number) => {
              const IconComponent = getIcon(value.icon);
              return (
                <Card 
                  key={index} 
                  className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Gradient Top Border */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${value.gradient}`}></div>
                  
                  <CardHeader className="text-center pb-2">
                    <div className={`inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br ${value.gradient} mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <IconComponent className="w-10 h-10 text-white" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-blue-600 text-white border-0 text-base py-2 px-6">
              <Users className="w-4 h-4 mr-2" />
              The STR Family
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-foreground">Meet Our</span>
              <span className="bg-gradient-to-r from-secondary to-tropical bg-clip-text text-transparent"> Amazing Team</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Dedicated professionals committed to making your Dominican experience extraordinary.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {teamData.map((member: any, index: number) => {
              const IconComponent = getIcon(member.icon);
              return (
                <Card 
                  key={index} 
                  className="group text-center border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 animate-fade-in-up overflow-hidden"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <CardHeader className="pb-4">
                    {/* Animated Avatar */}
                    <div className="relative mx-auto mb-4">
                      <div className="absolute inset-0 bg-gradient-to-br from-secondary to-tropical rounded-full blur-xl opacity-50 group-hover:opacity-80 transition-opacity"></div>
                      <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-secondary to-tropical flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-500">
                        <IconComponent className="w-12 h-12 text-white" />
                      </div>
                      {/* Ring Animation */}
                      <div className="absolute inset-0 rounded-full border-2 border-secondary/50 animate-ping opacity-20"></div>
                    </div>
                    
                    <CardTitle className="text-xl text-foreground">{member.name}</CardTitle>
                    <CardDescription className="text-secondary font-semibold text-base">{member.role}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 animate-fade-in-up">
              <Badge className="mb-6 bg-gradient-to-r from-palm to-tropical text-white border-0 text-base py-2 px-6">
                <ThumbsUp className="w-4 h-4 mr-2" />
                The STR Difference
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-foreground">Why Travelers</span>
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Choose Us</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Here's what sets Servicio Turístico Rachel apart from the rest.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whyChooseUsData.map((item: string, index: number) => (
                <div 
                  key={index} 
                  className="group flex items-center gap-4 p-5 rounded-xl bg-card shadow-sm hover:shadow-lg hover:bg-primary/5 transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-palm to-tropical flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-foreground font-medium text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-10 left-10 animate-float">
          <Sparkles className="w-12 h-12 text-white/30" />
        </div>
        <div className="absolute bottom-10 right-10 animate-float animation-delay-2000">
          <Star className="w-10 h-10 text-tropical/50" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Ready for Your
              <span className="block text-tropical">Caribbean Adventure?</span>
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-90 animate-fade-in-up animation-delay-200">
              Book your premium transfer or unforgettable tour today and discover why 
              thousands of travelers trust Servicio Turístico Rachel.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                <Link to="/transfers">Book a Transfer</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-10 py-6 hover:scale-105 transition-all">
                <Link to="/tours">Explore Tours</Link>
              </Button>
            </div>

            {/* Contact Info */}
            <div className="mt-16 pt-10 border-t border-white/20 animate-fade-in-up animation-delay-600">
              <p className="text-lg mb-6 font-semibold">Get in Touch Today</p>
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                <a href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`} className="flex items-center gap-3 hover:text-tropical transition-colors group">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-tropical/50 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span className="text-lg">{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
                </a>
                <a href={`mailto:${getConfig('contact_email', 'info@strtransfers.com')}`} className="flex items-center gap-3 hover:text-tropical transition-colors group">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-tropical/50 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span className="text-lg">{getConfig('contact_email', 'info@strtransfers.com')}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
